import { plural } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, ArrowLeftRight } from "lucide-react";
import ImportDialog, { ColumnNotes, Count, Decision, ProblemList, quoteList } from "@/components/ImportDialog";

/**
 * Employees from a spreadsheet: choose → check → import.
 *
 * Shakun HR's first attempt (14 Sep 2026) uploaded our own employee export and was refused
 * ten times by a toast reading "Missing required column: employee_code" — a name that was in
 * neither their file nor this dialog, which listed columns in its description as snake_case.
 * The server now reads the headers people actually write, and this dialog shows what it
 * understood BEFORE anything is saved: which sheet and columns it read, how many people are
 * ready, what needs fixing by row number, and the two decisions a real file needs (a column
 * swap it noticed, units and departments Masters does not have yet). Nothing is written until
 * every row passes; the preview is what makes that rule cheap to live with.
 *
 * The flow is `ImportDialog`'s, shared with the training import. What is here is the report.
 */
const ROUTES = {
  columns: "/employees/import/columns",
  template: "/employees/import-template.xlsx",
  preview: "/employees/import/preview",
  commit: "/employees/bulk-import",
};

export default function ImportEmployeesDialog({ open, onOpenChange, onImported }) {
  return (
    <ImportDialog
      open={open} onOpenChange={onOpenChange} onImported={onImported}
      title="Import employees from Excel"
      description="Fill in the template, or upload an Employees export. You will see what the file contains before anything is saved."
      routes={ROUTES} templateName="employees-import-template.xlsx"
      initialOptions={{ swap: false, create: false }}
      formFields={(o) => ({ swap_columns: o.swap, create_missing: o.create })}
      summary={(r) => `Sheet “${r.sheet}”, headers on row ${r.header_row} · ${plural(r.total_rows, "person", "people")}`}
      confirmLabel={(r) => `Import ${plural(r.ready_count, "employee")}`}
      successMessage={(d) => {
        const made = [d.units_created && plural(d.units_created, "unit"),
                      d.departments_created && plural(d.departments_created, "department")].filter(Boolean);
        return `Imported ${plural(d.created, "employee")}` + (made.length ? ` · created ${made.join(" and ")}` : "");
      }}
      guideFootnote="“Code”, “Name” and “DOJ” from an export work too."
      renderReport={({ report, options, busy, toggle }) => (
        <Report report={report} swap={options.swap} create={options.create} busy={busy} onToggle={toggle} />
      )}
    />
  );
}

function Report({ report, swap, create, busy, onToggle }) {
  const missing = report.missing_masters;
  const needsMasters = missing.units.length > 0 || missing.departments.length > 0;
  const waiting = create ? 0 : missing.rows;

  return (
    <div className={`space-y-4 transition-opacity ${busy ? "opacity-60" : ""}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="import-counts">
        <Count n={report.ready_count} label="ready to import" tone="text-emerald-700 dark:text-emerald-400" />
        <Count n={report.already_on_file.length} label="already on the register" />
        <Count n={report.problems.length} label={report.problems.length === 1 ? "row needs fixing" : "rows need fixing"}
               tone={report.problems.length ? "text-destructive-text" : ""} />
        <Count n={waiting} label="waiting on Masters" tone={waiting ? "text-amber-700 dark:text-amber-400" : ""} />
      </div>

      <ColumnNotes report={report} />

      {report.swap.suspected && (
        <Decision icon={ArrowLeftRight} testid="import-swap"
                  title={swap ? "Designation and Department are swapped for this import" : "Designation and Department look swapped"}
                  body={<>
                    Department holds {quoteList(report.swap.department_values)} — job titles — while Designation
                    holds {quoteList(report.swap.designation_values)}. Your file is not changed either way.
                  </>}
                  control={<Switch checked={swap} onCheckedChange={(v) => onToggle({ swap: v })} disabled={busy}
                                   aria-label="Swap Designation and Department" />}
                  controlLabel="Swap them" />
      )}

      {needsMasters && (
        <Decision icon={AlertTriangle} testid="import-masters"
                  title={`${[missing.units.length && plural(missing.units.length, "unit"),
                             missing.departments.length && plural(missing.departments.length, "department")]
                            .filter(Boolean).join(" and ")} not in Masters yet`}
                  body={<MastersList missing={missing} />}
                  control={<Checkbox checked={create} onCheckedChange={(v) => onToggle({ create: v === true })}
                                     disabled={busy} aria-label="Create them during this import" />}
                  controlLabel="Create them during this import"
                  footnote={report.swap.suspected && !swap
                    ? "Decide the swap above first — as the file stands, these job titles would become departments."
                    : "Check the spelling first: each name becomes a real unit or department, just as if it were added in Masters."} />
      )}

      <ProblemList problems={report.problems} />

      {report.ready_sample.length > 0 && (
        <div className="rounded-md border border-border" data-testid="import-sample">
          <div className="px-3 py-2 border-b border-border text-xs text-muted-foreground">
            How they will be added{report.ready_count > report.ready_sample.length &&
              ` — the first ${report.ready_sample.length} of ${report.ready_count}`}
          </div>
          {/* Five columns, not seven. Seven did not fit the dialog, and a plant name like
              HALOL-3-SEMICON-STAFF pushed Joined — the column most worth checking, since dates
              are read day first — out of sight. The code rides under the name and the unit
              under the department, where each is read together anyway. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  {/* On a phone Row and Designation fold away (Designation rides under the name),
                      so the three columns left — who, where, joined — fit 390px without the
                      joining date scrolling out of the table, which it did on HR's own file. */}
                  {[["Row", "hidden sm:table-cell"], ["Person", ""], ["Designation", "hidden sm:table-cell"],
                    ["Department · Unit", ""], ["Joined", ""]].map(([h, cls]) => (
                    <th key={h} className={`px-3 py-1.5 font-medium whitespace-nowrap ${cls}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {report.ready_sample.map((r) => (
                  <tr key={r.row} className="align-top">
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">{r.row}</td>
                    <td className="px-3 py-1.5">
                      <div>{r.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-mono">{r.employee_code}</span>
                        {r.designation && <span className="sm:hidden"> · {r.designation}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 hidden sm:table-cell">{r.designation || "—"}</td>
                    <td className="px-3 py-1.5">
                      <div>{r.department}</div>
                      <div className="text-xs text-muted-foreground break-words">{r.unit}</div>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.date_of_joining || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report.already_on_file.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="import-already">
          Left as they are, already on the register: {report.already_on_file.slice(0, 8)
            .map((a) => (a.on_register_as && a.on_register_as !== a.employee_code
              ? `${a.employee_code} (on the register as ${a.on_register_as})` : a.employee_code)).join(", ")}
          {report.already_on_file.length > 8 && ` and ${report.already_on_file.length - 8} more`}.
          Import only adds people; it never changes anybody.
        </p>
      )}
    </div>
  );
}

function MastersList({ missing }) {
  const byUnit = {};
  missing.departments.forEach((d) => { (byUnit[d.unit] ||= []).push(d.name); });
  missing.units.forEach((u) => { byUnit[u] ||= []; });
  return (
    <ul className="mt-1 space-y-0.5">
      {Object.entries(byUnit).map(([unit, names]) => (
        <li key={unit}>
          <span className="text-foreground">{unit}</span>
          {missing.units.includes(unit) && <span className="text-xs"> (new unit)</span>}
          {names.length > 0 && <>: {names.join(", ")}</>}
        </li>
      ))}
    </ul>
  );
}
